import { supabase } from '../lib/supabase';
import { withRetry, isDuplicateKey, isTransient, newId } from '../lib/netRetry';

/** מזהה המשתמש מהסשן המקומי (getSession — בלי סבב רשת ל-auth כמו getUser). */
async function currentUserId(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('no_session');
  return session.user.id;
}

/** שגיאת PostgREST לא נושאת את סטטוס ה-HTTP — רק message/details/hint/code; הסטטוס יושב על התשובה.
 *  בלעדיו 5xx של השער (Kong/Cloudflare — גוף HTML בלי code) ו-status 0 (כך supabase-js מסמן fetch שנכשל)
 *  לא מזוהים כחולפים, וב-Sentry הכשל נראה `code: '' status: null` — חסר ערך לאבחון. */
function withStatus<E extends object>(error: E, status: number): E & { status: number } {
  return Object.assign(error, { status });
}

export type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snack';

export interface EntryItem {
  name_he: string;
  qty_desc?: string;
  grams?: number;
  calories?: number;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
  /** יחידת מנה ידועה (למשל קרקר=30ג') — מאפשרת סטפר לפי יחידות במסך האישור */
  unit_grams?: number;
  unit_name?: string;
  /** אייקון ידני שנבחר למאכל האישי שממנו הגיע הפריט (אם נבחר) — שורד גם אם המאכל עצמו ישתנה */
  icon?: string;
  /** מזהה המאכל האישי שממנו הגיע הפריט (אם קיים) — כך שמועדפים/היסטוריה יודעים "לחזור" אליו
   *  לעריכה, גם בלי רשומת Food מוכנה על השורה עצמה. */
  customFoodId?: string;
  /** קוד המאכל מהמאגר הלאומי שממנו הגיע הפריט (אם קיים, ולא מאכל אישי) — כמו customFoodId
   *  אבל למאגר הלאומי: מאפשר לעריכה/שכפול-לעריכה לעבוד גם דרך מועדפים/היסטוריה. */
  foodCode?: number;
  /** **במועדפים בלבד:** שמות נוספים שבהם אותו מאכל מופיע ביומן ("ביצה שלמה" ← "ביצים") — כדי שסידור
   *  "הקבועים שלי" לפי שעה ותדירות יכיר את ההיסטוריה שלו. לא נכתב ליומן (toEntryItems מסיר). */
  aliases?: string[];
}

export interface Entry {
  id: string;
  user_id: string;
  eaten_at: string;
  meal_type: MealType | null;
  description: string | null;
  items: EntryItem[];
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  source: string | null;
  is_favorite: boolean;
}

export interface DayTotals {
  calories: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
}

/** גבולות היום המקומי (לא UTC!) כ-timestamptz לשאילתה */
function localDayRange(date = new Date()): { from: string; to: string } {
  const from = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const to = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** רישומי יום מסוים (ברירת מחדל: היום), לפי גבולות יום מקומיים */
export async function fetchEntriesForDate(date = new Date()): Promise<Entry[]> {
  const { from, to } = localDayRange(date);
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('litebite_entries')
    .select('*')
    .eq('user_id', userId)
    .gte('eaten_at', from)
    .lt('eaten_at', to)
    .order('eaten_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as Entry[];
}

export const fetchTodayEntries = (): Promise<Entry[]> => fetchEntriesForDate();

export function sumTotals(entries: Entry[]): DayTotals {
  return entries.reduce<DayTotals>(
    (acc, e) => ({
      calories: acc.calories + (e.calories ?? 0),
      proteinG: acc.proteinG + Number(e.protein_g ?? 0),
      carbsG: acc.carbsG + Number(e.carbs_g ?? 0),
      fatG: acc.fatG + Number(e.fat_g ?? 0),
    }),
    { calories: 0, proteinG: 0, carbsG: 0, fatG: 0 },
  );
}

export async function deleteEntry(id: string): Promise<void> {
  const userId = await currentUserId();
  // .select() כדי לתפוס מחיקה שלא פגעה בשום שורה — כמו ב-updateEntry. ‏DELETE שלא
  // מחק דבר חוזר **בלי error** (שורה שנמחקה במכשיר אחר, או שה-RLS לא נתן לגעת בה),
  // וה-UI היה מציג "נמחק ✓" / "בוטל ✓" על משהו שלא קרה.
  const { data, error } = await supabase.from('litebite_entries')
    .delete().eq('user_id', userId).eq('id', id).select('id');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('not_found');
}

/** שחזור רישום שנמחק (undo) — מחזיר את השורה המלאה כפי שהייתה, כולל id */
export async function restoreEntry(entry: Entry): Promise<void> {
  await withRetry(async () => {
    const { error, status } = await supabase.from('litebite_entries').insert(entry);
    // ה-id הוא של השורה המקורית (מהשרת) — אם היא כבר קיימת (ניסיון קודם שתשובתו אבדה, "ביטול"
    // שנלחץ פעמיים) השחזור הושג. כאן זה נכון גם בשידור הראשון, כי המזהה לא נוצר עכשיו.
    if (error && !isDuplicateKey(error)) throw withStatus(error, status);
  });
}

export interface NewEntry {
  meal_type: MealType | null;
  description: string | null;
  items: EntryItem[];
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  source: 'ai_text' | 'manual' | 'favorite' | 'photo' | 'chat';
}

/**
 * זמן האכילה עבור יום נבחר: אם היום — עכשיו; אחרת אותו יום עם שעת ההוספה הנוכחית
 * (כדי ששורות באותו יום ישמרו סדר כרונולוגי, ויפלו בבירור בתוך היום המקומי הנכון).
 */
export function eatenAtForDay(day: Date): string {
  const now = new Date();
  const isToday =
    day.getFullYear() === now.getFullYear() &&
    day.getMonth() === now.getMonth() &&
    day.getDate() === now.getDate();
  if (isToday) return now.toISOString();
  return new Date(
    day.getFullYear(), day.getMonth(), day.getDate(),
    now.getHours(), now.getMinutes(), now.getSeconds(),
  ).toISOString();
}

/** מוסיף רישום ומחזיר את ה-id שלו (ל-undo של הוספה מיידית). */
export async function addEntry(entry: NewEntry, eatenAtIso?: string): Promise<string> {
  const [id] = await addEntries([entry], eatenAtIso);
  return id;
}

// ---------- הוספה עם ריטריי בלי כפילות (המנגנון: src/lib/netRetry.ts) ----------

/**
 * המזהים של הכוונה האחרונה שנכשלה מסיבה חולפת. שלושת הניסיונות האוטומטיים מצמצמים אבל לא מבטלים
 * את המקרה שכל התשובות אבדו והשרת כן כתב: המשתמשת רואה "נכשל" ולוחצת שוב (בדיוק הדיווח המקורי —
 * "לחצתי כמה פעמים"), ובלי זה הלחיצה השנייה הייתה יוצרת מזהים חדשים ורישום כפול. איתו היא שולחת
 * את **אותם** מזהים, ואם הראשונים כבר נכתבו — 23505 = הצלחה. חלון קצר בכוונה: אחרי דקה אותו תוכן
 * הוא כוונה חדשה (עוד קפה), לא ניסיון חוזר.
 *
 * ⚠️ **מפה ולא משבצת אחת:** ‏busySaving (מסך ההוספה) ו-busyAction (הבית) הם שני מנעולים שאינם
 * מכירים זה את זה, ו-closeAddFlow משחרר את הראשון בזמן ששמירה עוד רצה. שמירה שנייה הייתה דורסת
 * את המזהים של הראשונה, והלחיצה החוזרת עליה הייתה יוצרת כפילות.
 * ⚠️ **ה-`at` מתרענן ברגע הכשל ולא ברגע השליחה:** אין timeout על ה-fetch (החלטה מפורשת), וכשל
 * איטי נמשך דקות — כלומר ה-TTL היה נשרף על משך הניסיונות עצמם ופג בדיוק ברגע שההודעה מופיעה.
 * וזה בדיוק המקרה המסוכן: כשל איטי = הבקשה כן הגיעה לשרת = סביר שהוא כתב.
 */
const memo = new Map<string, { ids: string[]; at: number }>();
const RETRY_MEMO_MS = 60_000;

/** קריאה מהמפה עם פינוי רשומות שפג תוקפן (המפה קטנה — כמה כוונות בדקה לכל היותר). */
function memoGet(sig: string): { ids: string[]; at: number } | null {
  const now = Date.now();
  for (const [k, v] of memo) if (now - v.at >= RETRY_MEMO_MS) memo.delete(k);
  return memo.get(sig) ?? null;
}

/** חתימת הכוונה: התוכן + היום המקומי. לא שעת האכילה המדויקת — eatenAtForDay מחזיר "עכשיו", שמשתנה
 *  בין לחיצה ללחיצה; אבל יום אחר הוא כוונה אחרת (אותו קפה, אתמול). */
/** ⚠️ החתימה כוללת את המשתמש: זהו מצב ברמת מודול, והוא שורד יציאה והתחברות באותה לשונית
 *  (אין reload בקוד — ראו main.ts). בלי ה-user_id, משתמש שני שמתעד את אותו תוכן באותו יום
 *  תוך דקה היה מקבל את המזהים של הראשון, ו-23505 היה נחשב "כבר נשמר" — ארוחה שנעלמת בשקט. */
function intentSig(userId: string, entries: NewEntry[], eatenAtIso: string): string {
  return `${userId}|${localDayKey(eatenAtIso)}|${JSON.stringify(entries)}`;
}

/** איפוס זיכרון הכתיבה ביציאה (נקרא מ-main.ts לצד invalidateTargets/invalidateMealPlan). */
export function invalidateWriteMemo(): void {
  memo.clear();
}

/** כמו addEntry, אבל לכמה רישומים (כל אחד שורה נפרדת) בסבב רשת אחד — כולם עם אותו eaten_at.
 *  מחזיר ids באותו סדר שהוזנו. */
export async function addEntries(entries: NewEntry[], eatenAtIso?: string): Promise<string[]> {
  if (!entries.length) return [];
  const eaten_at = eatenAtIso ?? new Date().toISOString();
  // ⚠️ currentUserId נכשל ב-no_session כשרענון הטוקן נופל ברשת — התרחיש הנפוץ של טלפון
  // שהיה בכיס. מחוץ ללולאה הוא היה זורק בלי ניסיון שני, בזמן ש-isTransient מסמן אותו כחולף.
  const userId = await withRetry(() => currentUserId());
  const sig = intentSig(userId, entries, eaten_at);
  const retryOf = memoGet(sig);
  // ⚠️ הכוונה נרשמת **לפני** השליחה, לא רק אחרי כשל: שמירה שעדיין רצה (המשתמשת סגרה את
  // המסך כי "נראה תקוע" ופתחה מחדש) לא הייתה מוכרת לאף מנגנון, והלחיצה השנייה הייתה מקבלת
  // מזהים טריים ⇒ שתי שורות. עכשיו אותו תוכן באותה דקה מקבל את אותם מזהים בכל מקרה.
  // ה-id נוצר כאן ולא בשרת: בקליטה חלשה בקשה יכולה להצליח בשרת ותשובתה ללכת לאיבוד,
  // וריטריי עם אותם מזהים ייתקל ב-23505 במקום ליצור רישום כפול ביומן.
  const ids = retryOf?.ids ?? entries.map(() => newId());
  // מחולל שבור שמחזיר תמיד את אותו ערך הופך "23505 = כבר נשמר" למסלול של ארוחות שנעלמות.
  // הבדיקה זולה ותופסת אותו בשמירה מרובת-פריטים (המקרה הנפוץ) לפני שנשלח משהו.
  if (new Set(ids).size !== ids.length) throw new Error('id_generator_broken');
  memo.set(sig, { ids, at: Date.now() });
  try {
    await withRetry(async (attempt) => {
      const { error, status } = await supabase.from('litebite_entries')
        .insert(entries.map((e, i) => ({ ...e, id: ids[i], user_id: userId, eaten_at })));
      if (!error) return;
      // מפתח כפול = הרישום כבר שם — אבל רק כשהמזהים האלה כבר נשלחו פעם (ניסיון קודם בלולאה, או
      // הלחיצה הקודמת שנכשלה). ⚠️ 23505 בשידור הראשון של מזהה טרי אינו "כבר נשמר" אלא באג (מחולל
      // מזהים שבור, למשל) — וחייב להיראות ככשל, לא כ"נוסף ✓" שמעלים את הארוחה בשקט.
      // ה-insert של כמה שורות הוא משפט אחד ⇒ אטומי: או שכולן נכתבו או שאף אחת — אין "חלקי".
      if (isDuplicateKey(error) && (attempt > 0 || retryOf !== null)) return;
      throw withStatus(error, status);
    });
  } catch (e) {
    // כשל שאינו חולף (הפרת אילוץ) לא ייכתב לעולם — אין טעם לשמר לו מזהים
    if (!isTransient(e)) memo.delete(sig);
    // כשל חולף: מרעננים את השעון לרגע הכשל, כי משם מתחיל חלון הלחיצה החוזרת של המשתמשת
    else memo.set(sig, { ids, at: Date.now() });
    throw e;
  }
  memo.delete(sig);
  return ids;
}

/** שינוי שעה לקבוצת רישומים (תפריט ⋯ בבית) — כולם מקבלים את אותה שעה */
/** העברת רישומים לסוג ארוחה אחר (אותו יום). מסונן ב-user_id ומנוסה שוב — אידמפוטנטי. */
export async function setEntriesMeal(ids: string[], mealType: MealType): Promise<void> {
  if (!ids.length) return;
  await withRetry(async () => {
    const userId = await currentUserId();
    const { data, error, status } = await supabase.from('litebite_entries')
      .update({ meal_type: mealType }).eq('user_id', userId).in('id', ids).select('id');
    if (error) throw withStatus(error, status);
    // ⚠️ עדכון שלא פגע בשום שורה חוזר בלי שגיאה — וה-UI היה מציג "הועבר ✓" על כלום
    if (!data || data.length === 0) throw new Error('not_found');
  });
}

export async function setEntriesTime(ids: string[], eatenAtIso: string): Promise<void> {
  if (!ids.length) return;
  const userId = await currentUserId();
  const { error } = await supabase.from('litebite_entries')
    .update({ eaten_at: eatenAtIso }).eq('user_id', userId).in('id', ids);
  if (error) throw error;
}

/** עדכון ארוחה קיימת (עריכה) — שומר את eaten_at המקורי אלא אם צוין אחרת.
 *  עדכון הוא אידמפוטנטי (אותו patch על אותה שורה), לכן ריטריי על כשל חולף בטוח בלי מזהים. */
export async function updateEntry(id: string, patch: Partial<NewEntry>): Promise<void> {
  await withRetry(async () => {
    // .select() כדי לתפוס no-op שקט (שורה שנמחקה במכשיר אחר / RLS) ולא להציג "עודכן ✓" סתם
    const userId = await currentUserId();
    const { data, error, status } = await supabase.from('litebite_entries')
      .update(patch).eq('user_id', userId).eq('id', id).select('id');
    if (error) throw withStatus(error, status);
    if (!data || data.length === 0) throw new Error('not_found');
  });
}

const RECENT_COLS = 'id, user_id, eaten_at, meal_type, description, items, calories, protein_g, carbs_g, fat_g, source, is_favorite';

/** ארוחות אחרונות ייחודיות (לפי תיאור) מ-30 הימים האחרונים — להוספה חוזרת בלחיצה */
export async function fetchRecentMeals(limit = 6): Promise<Entry[]> {
  const since = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('litebite_entries')
    .select(RECENT_COLS)
    .eq('user_id', userId)
    .gte('eaten_at', since)
    .order('eaten_at', { ascending: false })
    .limit(60);
  if (error) throw error;
  const seen = new Set<string>();
  const unique: Entry[] = [];
  for (const e of (data ?? []) as Entry[]) {
    const key = (e.description ?? '').trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(e);
    if (unique.length >= limit) break;
  }
  return unique;
}

/**
 * ארוחות אחרונות מקובצות לפי יום+סוג ארוחה: מנות שתועדו בנפרד (הוספה מיידית)
 * מתאחדות ל"ארוחה" אחת — כמו הקיבוץ במסך הבית. מוחזרות כ-Entry סינתטי
 * (items ממוזגים, קלוריות/מקרו מסוכמים, eaten_at של הרישום האחרון בקבוצה).
 */
export async function fetchRecentGrouped(limit = 6): Promise<Entry[]> {
  const since = new Date(Date.now() - 14 * 24 * 3600 * 1000).toISOString();
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('litebite_entries')
    .select(RECENT_COLS)
    .eq('user_id', userId)
    .gte('eaten_at', since)
    .order('eaten_at', { ascending: false })
    .limit(80);
  if (error) throw error;
  const groups = new Map<string, Entry>();
  for (const e of (data ?? []) as Entry[]) {
    const key = `${localDayKey(e.eaten_at)}|${e.meal_type ?? 'snack'}`;
    const items = e.items?.length
      ? e.items
      : [{ name_he: e.description ?? 'ארוחה', calories: e.calories,
          protein_g: e.protein_g ?? undefined, carbs_g: e.carbs_g ?? undefined, fat_g: e.fat_g ?? undefined }];
    const cur = groups.get(key);
    if (!cur) {
      groups.set(key, { ...e, items: [...items] });
    } else {
      // הרשומות ממוינות מהחדש לישן — cur מחזיק את eaten_at האחרון; מוסיפים לתחילת הרשימה
      cur.items = [...items, ...cur.items];
      cur.calories += e.calories ?? 0;
      // סכימה מספרית ישירה — בלי "|| null" שמאבד סכומי אפס לגיטימיים
      cur.protein_g = Number(cur.protein_g ?? 0) + Number(e.protein_g ?? 0);
      cur.carbs_g = Number(cur.carbs_g ?? 0) + Number(e.carbs_g ?? 0);
      cur.fat_g = Number(cur.fat_g ?? 0) + Number(e.fat_g ?? 0);
      cur.description = cur.items.map((i) => i.name_he).join(', ');
    }
  }
  return [...groups.values()].slice(0, limit);
}

/** ארוחות תדירות — הכי-נלוגות ב-60 הימים האחרונים (לפחות פעמיים), מהנפוץ לנדיר */
export async function fetchFrequentMeals(limit = 6): Promise<Entry[]> {
  const since = new Date(Date.now() - 60 * 24 * 3600 * 1000).toISOString();
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('litebite_entries')
    .select(RECENT_COLS)
    .eq('user_id', userId)
    .gte('eaten_at', since)
    .order('eaten_at', { ascending: false })
    .limit(250);
  if (error) throw error;
  const byDesc = new Map<string, { entry: Entry; count: number }>();
  for (const e of (data ?? []) as Entry[]) {
    const key = (e.description ?? '').trim();
    if (!key) continue;
    const cur = byDesc.get(key);
    if (cur) cur.count++;
    else byDesc.set(key, { entry: e, count: 1 });
  }
  return [...byDesc.values()]
    .filter((v) => v.count >= 2)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
    .map((v) => v.entry);
}

// ---------- סטטיסטיקת מאכלים (לחיפוש v4: "כמות מהפעם הקודמת" + "תדירים בשעה הזו") ----------

export interface FoodStat {
  name: string;
  /** הפריט כפי שנאכל בפעם האחרונה — כולל הכמות (grams/qty_desc) והערכים לאותה כמות */
  item: EntryItem;
  count: number;
  lastEatenAt: string;
  /** סוג הארוחה של הפעם האחרונה — ברירת מחדל בהוספה חוזרת */
  meal_type: MealType | null;
  hours: number[];
  /** סוג הארוחה בכל פעם שנאכל (מקביל ל-hours; null → נשנוש) — לסידור "הקבועים שלי" לפי הארוחה שנבחרה */
  meals: MealType[];
}

export interface FoodStats {
  /** לכל שם מאכל — הכמות האחרונה והתדירות (למילוי "בפעם הקודמת" בתוצאות חיפוש) */
  byName: Map<string, FoodStat>;
  /** מאכלים שנאכלים סביב השעה הנוכחית (±2ש'), מהתדיר לנדיר; מרופד בתדירים כלליים */
  frequentNow: FoodStat[];
}

/** בונה סטטיסטיקה פר-מאכל מ-60 הימים האחרונים (שאילתה אחת, עיבוד בצד הלקוח). */
export async function fetchFoodStats(): Promise<FoodStats> {
  const since = new Date(Date.now() - 60 * 24 * 3600 * 1000).toISOString();
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('litebite_entries')
    .select('eaten_at, meal_type, items')
    .eq('user_id', userId)
    .gte('eaten_at', since)
    .order('eaten_at', { ascending: false })
    .limit(250);
  if (error) throw error;
  const byName = new Map<string, FoodStat>();
  for (const e of (data ?? []) as Array<{ eaten_at: string; meal_type: MealType | null; items: EntryItem[] | null }>) {
    const h = new Date(e.eaten_at).getHours();
    for (const it of e.items ?? []) {
      const name = (it.name_he ?? '').trim();
      if (!name || !it.calories) continue;
      const cur = byName.get(name);
      if (cur) {
        cur.count++;
        cur.hours.push(h);
        cur.meals.push(e.meal_type ?? 'snack');
      } else {
        // הרשומות ממוינות מהחדש לישן — הראשונה שנפגוש היא "הפעם האחרונה"
        byName.set(name, { name, item: it, count: 1, lastEatenAt: e.eaten_at, meal_type: e.meal_type, hours: [h], meals: [e.meal_type ?? 'snack'] });
      }
    }
  }
  return { byName, frequentNow: frequentAt(byName.values(), new Date().getHours()) };
}

/**
 * מאכלים שנאכלים סביב שעה נתונה (±2ש', לפחות פעמיים), מהתדיר לנדיר; מרופד בתדירים כלליים. עד 6.
 * ⚠️ לקרוא עם השעה של **רגע ההצגה** — לא לשמור את התוצאה: ב-PWA שנשאר פתוח ימים, רשימה שחושבה
 * בבוקר הוצגה גם בערב (ביקורת 29.09).
 */
export function frequentAt(stats: Iterable<FoodStat>, hour: number): FoodStat[] {
  const dist = (a: number, b: number) => Math.min(Math.abs(a - b), 24 - Math.abs(a - b));
  const scored = [...stats].map((s) => ({ s, near: s.hours.filter((h) => dist(h, hour) <= 2).length }));
  let out = scored.filter((x) => x.near >= 2).sort((a, b) => b.near - a.near).map((x) => x.s);
  if (out.length < 3) {
    const rest = scored
      .filter((x) => x.near < 2 && x.s.count >= 2)
      .sort((a, b) => b.s.count - a.s.count)
      .map((x) => x.s);
    out = [...out, ...rest];
  }
  return out.slice(0, 6);
}

/** העתקת כל הארוחות מיום אחד ליום אחר. מחזיר כמה הועתקו.
 *  דרך addEntries — אותו ריטריי-בלי-כפילות כמו כל הוספה אחרת. */
export async function copyDay(fromDay: Date, toDay: Date): Promise<number> {
  const entries = await fetchEntriesForDate(fromDay);
  if (!entries.length) return 0;
  const rows: NewEntry[] = entries.map((e) => ({
    meal_type: e.meal_type, description: e.description, items: e.items, calories: e.calories,
    protein_g: e.protein_g, carbs_g: e.carbs_g, fat_g: e.fat_g, source: 'favorite',
  }));
  const ids = await addEntries(rows, eatenAtForDay(toDay));
  return ids.length;
}

export interface DailyTargets {
  calories: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
}

// קאש-בזיכרון ליעדים — משתנים רק בשמירת פרופיל. חוסך סבב רשת בכל כניסה לבית/התקדמות.
let targetsCache: DailyTargets | null | undefined;

/** לאיפוס הקאש אחרי שמירת פרופיל (נקרא מ-profile.ts). */
export function invalidateTargets(): void {
  targetsCache = undefined;
}

/** יעדי היום מהפרופיל; null כשאין פרופיל עדיין (ה-UI מפנה להגדרות). מוחזק בקאש. */
export async function fetchTargets(): Promise<DailyTargets | null> {
  if (targetsCache !== undefined) return targetsCache;
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('litebite_profiles')
    .select('calorie_target, protein_target_g, carbs_target_g, fat_target_g')
    .eq('user_id', userId)
    .maybeSingle();
  // שגיאה אמיתית (רשת/הרשאה) נזרקת — כדי שהמסך יראה "ניסיון חוזר" ולא מסך אונבורדינג מטעה.
  // null מוחזר רק כשאין פרופיל (data ריק) = משתמש חדש באמת.
  if (error) throw error;
  if (!data || data.calorie_target == null) {
    targetsCache = null;
    return null;
  }
  targetsCache = {
    calories: data.calorie_target,
    proteinG: data.protein_target_g ?? 0,
    carbsG: data.carbs_target_g ?? 0,
    fatG: data.fat_target_g ?? 0,
  };
  return targetsCache;
}

// ---------- יעד מותאם לפי משקל בפועל ----------

export interface AdaptiveProfileInfo {
  sex: 'male' | 'female';
  birthdate: string; // YYYY-MM-DD
  heightCm: number;
  weeklyRateKg: number;
  proteinTargetG: number;
  goalWeightKg: number | null;
}

/** שדות הפרופיל הדרושים לחישוב היעד המותאם (goals.computeAdaptiveTarget) ולתאריך היעד
 *  המשוער. null אם חסר מידע קריטי בפרופיל (למשל נרשם/ה לפני שדות אלה נוספו) —
 *  ה-UI פשוט מדלג על ההצעה. */
export async function fetchAdaptiveProfileInfo(): Promise<AdaptiveProfileInfo | null> {
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('litebite_profiles')
    .select('sex, birthdate, height_cm, weekly_rate_kg, protein_target_g, goal_weight_kg')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  if (!data || !data.sex || !data.birthdate || data.height_cm == null || data.weekly_rate_kg == null) return null;
  return {
    sex: data.sex, birthdate: data.birthdate, heightCm: data.height_cm,
    weeklyRateKg: data.weekly_rate_kg, proteinTargetG: data.protein_target_g ?? 0,
    goalWeightKg: data.goal_weight_kg ?? null,
  };
}

/** אימוץ יעד מוצע (מהתאמת המשקל בפועל) — הופך אותו ליעד מפורש (manual_targets),
 *  ומאפס את הקאש כדי שהבית/ההתקדמות ישקפו אותו מיד.
 *  מסונן ב-user_id מפורש (לא סומכים רק על RLS — אדמין רואה כמה שורות). */
export async function acceptAdaptiveTarget(t: { calories: number; proteinG: number; carbsG: number; fatG: number }): Promise<void> {
  const userId = await currentUserId();
  const { error } = await supabase.from('litebite_profiles').update({
    calorie_target: t.calories, protein_target_g: t.proteinG,
    carbs_target_g: t.carbsG, fat_target_g: t.fatG, manual_targets: true,
  }).eq('user_id', userId);
  if (error) throw error;
  invalidateTargets();
}

/** YYYY-MM-DD מקומי מ-timestamp */
function localDayKey(ts: string): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** רצף ימי תיעוד רצופים המסתיים היום או אתמול (למוטיבציה בטבעת הבית). */
export async function fetchStreak(): Promise<number> {
  const since = new Date(Date.now() - 60 * 24 * 3600 * 1000).toISOString();
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('litebite_entries')
    .select('eaten_at')
    .eq('user_id', userId)
    .gte('eaten_at', since)
    .order('eaten_at', { ascending: false });
  if (error || !data) return 0;
  const days = new Set(data.map((r) => localDayKey((r as { eaten_at: string }).eaten_at)));
  const p = (n: number) => String(n).padStart(2, '0');
  const key = (d: Date) => `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  const today = new Date();
  const todayKey = key(today);
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  // הרצף חי אם תועד היום או אתמול; מתחילים לספור מהיום המתועד האחרון
  if (!days.has(todayKey) && !days.has(key(yesterday))) return 0;
  let streak = 0;
  const cursor = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (!days.has(todayKey)) cursor.setDate(cursor.getDate() - 1); // הרצף מסתיים אתמול
  while (days.has(key(cursor))) {
    streak++;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}
